/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { Tab, Tabs, Tooltip } from "@material-ui/core";
import React, { useState, useRef, useCallback, cloneElement, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { ConfirmDialog } from "../ConfirmDialog/ConfirmDialog";
import { useQueryParams } from "../../hooks";
import ShowSnackbar from "./ShowSnackbar";

const PageTabs = ({
	tabs = [],
	requireConfirmOnChange,
	className,
	onTabChange = () => {},
	onTabContentModified = () => {},
	selectedTabInQueryString = false,
}) => {
	const [notification, setNotification] = useState({ message: "", severity: "success" });
	const [selectedTab, setSelectedTab] = useState("");
	const [tabConfirmOpen, setTabConfirmOpen] = useState(false);
	const [tabContentModified, setTabContentModified] = useState(false);
	const history = useHistory();
	const query = useQueryParams();
	const queryTab = query.get("tab");

	const activateTab = useCallback(
		(tabName) => {
			if (selectedTabInQueryString) {
				history.push(`?tab=${tabName}`);
			}

			setTabContentModified(false);
			onTabChange(tabName);
			const matchingTab = tabs.find((tab) => tab.id === tabName);
			setSelectedTab(matchingTab);
		},
		[history, onTabChange, selectedTabInQueryString, tabs],
	);

	useEffect(() => {
		onTabContentModified(tabContentModified);
	}, [onTabContentModified, tabContentModified]);

	useEffect(() => {
		let matchingTab = tabs.find((tab) => tab.default);
		if (selectedTabInQueryString && queryTab) {
			matchingTab = tabs.find((tab) => tab.id === queryTab);
		} else if (selectedTab) {
			matchingTab = tabs.find((tab) => tab.id === selectedTab.id);
		}
		setSelectedTab(matchingTab);
	}, [tabs, selectedTabInQueryString, queryTab, selectedTab, setSelectedTab]);

	const switchingTab = useRef(null);

	const handleSnackBarClose = () => {
		setNotification({ message: "", severity: "success" });
	};

	const handleTabChange = (_event, newValue) => {
		if (requireConfirmOnChange && selectedTab !== newValue && tabContentModified) {
			switchingTab.current = newValue;
			setTabConfirmOpen(true);
		} else {
			activateTab(newValue);
		}
	};

	const setSnackbar = useCallback((message, severity) => {
		setNotification({ message, severity });
	}, []);

	if (!selectedTab?.component) {
		return null;
	}

	return (
		<>
			<Tabs
				value={selectedTab.id}
				onChange={handleTabChange}
				className={className || "w-100"}
				style={{ color: "#941012" }}
				variant="fullWidth"
				centered
				aria-label="tabs"
			>
				{tabs?.map((tab, i) => (
					<Tab
						key={tab.id}
						data-test-id={`page-tab-${i}`}
						label={
							tab.tooltip ? (
								<Tooltip title={tab.tooltip} arrow>
									<span>{tab.label}</span>
								</Tooltip>
							) : (
								tab.label
							)
						}
						value={tab.id}
					/>
				))}
			</Tabs>

			{/* tab content */}
			{cloneElement(selectedTab.component, {
				showSnackbar: setSnackbar,
				setTabModified: setTabContentModified,
			})}

			<ShowSnackbar message={notification.message} severity={notification.severity} onClose={handleSnackBarClose} />
			<ConfirmDialog
				title="Switch Tab?"
				message="You have unsaved changes. Are you sure you wish to move without saving?"
				confirmOpen={tabConfirmOpen}
				onClose={async (confirmed) => {
					if (tabContentModified && confirmed && switchingTab.current) {
						activateTab(switchingTab.current);
					}
					setTabConfirmOpen(false);
				}}
			/>
		</>
	);
};

export default PageTabs;
